import { AccountCategory, type AccountSummary } from '@fruitful/types';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo, useState } from 'react';

import {
    adminUserAtom,
    selectedMembershipAtom,
} from '../../../entities/entityAtoms';
import { getAccountCategoryPickerOptions } from '../../../entities/membership/components/accountModalFormUtils';
import { useUpdateManualAccountAndRefetch } from '../../../entities/membership/hooks/externalAccounts/useUpdateManualAccount';
import { useUpsertOrionAccountAndRefetch } from '../../../entities/membership/hooks/externalAccounts/useUpsertOrionAccount';
import { useUpsertQuilttAccountAndRefetch } from '../../../entities/membership/hooks/externalAccounts/useUpsertQuilttAccount';
import { useUpsertSpinwheelCreditCardAccountAndRefetch } from '../../../entities/membership/hooks/externalAccounts/useUpsertSpinwheelCreditCardAccount';
import { useUpsertSpinwheelLoanAccountAndRefetch } from '../../../entities/membership/hooks/externalAccounts/useUpsertSpinwheelLoanAccount';
import { useUpsertSyncteraAccountAndRefetch } from '../../../entities/membership/hooks/externalAccounts/useUpsertSyncteraAccount';
import { useMembershipHolisticSummary } from '../../../entities/membership/hooks/useMembershipHolisticSummary';
import { useMoneyMapperIncomePickerItems } from '../take-home-pay/useMoneyMapperIncomePickerItems';
import {
    flattenHolisticAccounts,
    shouldShowInMoneyMapperCurrentAssets,
} from './moneyMapperCurrentAssetsUtils';
import {
    persistHolisticCategoryForAccount,
    persistLinkedAccountNicknameForAccount,
} from './moneyMapperLinkedHolisticPersist';
import {
    buildUpdateManualBodyFromRow,
    type ManualAccountRowFormState,
} from './moneyMapperManualAccountRowForm';

export const useMoneyMapperCurrentAssetsSection = ({
    isSaving,
}: {
    isSaving: boolean;
}) => {
    const membership = useAtomValue(selectedMembershipAtom);
    const adminUser = useAtomValue(adminUserAtom);

    const canEditAccounts =
        adminUser.authorization.selectedMembership.accounts.canEdit;
    const canEditGeneral =
        adminUser.authorization.selectedMembership.general.canEdit;
    /** Row menu + Add asset (match take-home pay using general.canEdit). */
    const canOpenAccountMenusAndAdd = canEditAccounts || canEditGeneral;

    const { isLoadingHolisticSummary, isHolisticSummaryError } =
        useMembershipHolisticSummary(membership?.id ?? null);

    const { ownerPickerItems } = useMoneyMapperIncomePickerItems(membership);

    /** Full enum: shared modal helper omits OTHER/UNKNOWN; current-assets picker must match server values. */
    const holisticCategoryPickerItems = useMemo(
        () => [
            ...getAccountCategoryPickerOptions(),
            {
                label: 'Other',
                value: AccountCategory.OTHER,
            },
            {
                label: 'Unknown',
                value: AccountCategory.UNKNOWN,
            },
        ],
        [],
    );

    const { updateManualAccountAsync } = useUpdateManualAccountAndRefetch(
        membership?.id ?? null,
    );
    const { upsertQuilttAccountAsync } = useUpsertQuilttAccountAndRefetch(
        membership?.id ?? null,
    );
    const { upsertSyncteraAccountAsync } = useUpsertSyncteraAccountAndRefetch(
        membership?.id ?? null,
    );
    const { upsertOrionAccountAsync } = useUpsertOrionAccountAndRefetch(
        membership?.id ?? null,
    );
    const { upsertSpinwheelCreditCardAccountAsync } =
        useUpsertSpinwheelCreditCardAccountAndRefetch(membership?.id ?? null);
    const { upsertSpinwheelLoanAccountAsync } =
        useUpsertSpinwheelLoanAccountAndRefetch(membership?.id ?? null);

    const allHolisticAccounts = useMemo(
        () => flattenHolisticAccounts(membership?.holisticSummary),
        [membership?.holisticSummary],
    );

    const accounts = useMemo(
        () => allHolisticAccounts.filter(shouldShowInMoneyMapperCurrentAssets),
        [allHolisticAccounts],
    );

    /** Manual + linked table edits follow AccountEditModal permissions (not gated on Fruitful holistic). */
    const canEditManualAccountInline = canOpenAccountMenusAndAdd;

    const holisticPersistDeps = useMemo(
        () => ({
            upsertQuilttAccountAsync,
            upsertSyncteraAccountAsync,
            upsertOrionAccountAsync,
            upsertSpinwheelCreditCardAccountAsync,
            upsertSpinwheelLoanAccountAsync,
        }),
        [
            upsertOrionAccountAsync,
            upsertQuilttAccountAsync,
            upsertSpinwheelCreditCardAccountAsync,
            upsertSpinwheelLoanAccountAsync,
            upsertSyncteraAccountAsync,
        ],
    );

    const onPersistManualAccount = useCallback(
        async (account: AccountSummary, form: ManualAccountRowFormState) => {
            const body = buildUpdateManualBodyFromRow(account, form);
            if (body == null) {
                return;
            }
            await updateManualAccountAsync(body);
        },
        [updateManualAccountAsync],
    );

    const onPersistLinkedHolisticCategory = useCallback(
        async (account: AccountSummary, holisticCategory: AccountCategory) => {
            await persistHolisticCategoryForAccount(
                account,
                holisticCategory,
                holisticPersistDeps,
            );
        },
        [holisticPersistDeps],
    );

    const onPersistLinkedAccountNickname = useCallback(
        async (account: AccountSummary, nextName: string) => {
            await persistLinkedAccountNicknameForAccount(
                account,
                nextName,
                holisticPersistDeps,
            );
        },
        [holisticPersistDeps],
    );

    const [menuAccount, setMenuAccount] = useState<AccountSummary | null>(null);
    const [showCreateManualModal, setShowCreateManualModal] = useState(false);

    const modalBusy = menuAccount != null || showCreateManualModal;

    return {
        membership,
        canOpenAccountMenusAndAdd,
        canEditManualAccountInline,
        isSaving,
        isLoadingHolisticSummary,
        isHolisticSummaryError,
        accounts,
        ownerPickerItems,
        holisticCategoryPickerItems,
        onPersistManualAccount,
        onPersistLinkedHolisticCategory,
        onPersistLinkedAccountNickname,
        menuAccount,
        setMenuAccount,
        showCreateManualModal,
        setShowCreateManualModal,
        modalBusy,
    };
};
